// RoomViewer — "See it in your space" feature for the artwork detail page.
// Shows the artwork composited into a series of room scenes (rapai coastal
// reference + 5 curated interiors) with a frame style picker (oak / black /
// gold / unframed). Buyers use this to visualise the work in their home
// before checkout, which materially improves conversion on art marketplaces.

import { useState, useEffect } from "react";
import RoomScene from "./RoomScene";
import { useRoomScenes, FRAME_STYLES, DEFAULT_FRAME_ID } from "../lib/roomScenes";
import { ChevronLeft, ChevronRight, Home, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RoomViewer({ artworkId = "", artworkUrl, artworkTitle = "", medium = "", dimensions = "", gallery = null }) {
  const ROOM_SCENES = useRoomScenes(medium || undefined);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [frameId, setFrameId] = useState(DEFAULT_FRAME_ID);
  const [showScale, setShowScale] = useState(true);   // plinth only
  const [rotate, setRotate] = useState(0);            // plinth only, -60..60
  // v1.45 — per-scene composite URL cache and inflight flag.
  const [composites, setComposites] = useState({});   // { [scene_id]: url }
  const [composing, setComposing] = useState(false);
  const [composeError, setComposeError] = useState("");

  // Derived values used by hooks below (safe when scene list is empty —
  // `sceneCandidate` is undefined, isPlinthCandidate = false, so the
  // effect below no-ops until scenes load).
  const sceneCandidate = ROOM_SCENES[Math.min(sceneIdx, Math.max(0, ROOM_SCENES.length - 1))];
  const isPlinthCandidate = sceneCandidate?.type === "plinth";
  const candidateSceneId = sceneCandidate?.id;

  // v1.45 — fetch a photoreal composite when the user selects a
  // sculpture scene. First call takes ~15-30 s (Gemini); subsequent
  // are cached server-side and return instantly. Hook is declared
  // unconditionally so React sees a stable hook order on every render
  // (needed for the CRA production build to pass rules-of-hooks).
  useEffect(() => {
    if (!isPlinthCandidate || !artworkId || !candidateSceneId) return;
    // Clear any stale error from a previous scene so the "unavailable"
    // banner doesn't linger over a good composite.
    setComposeError("");
    if (composites[candidateSceneId]) return; // already have it
    let cancelled = false;
    setComposing(true);
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/artworks/${artworkId}/visualise/${candidateSceneId}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled && data?.image_url) {
          setComposites((c) => ({ ...c, [candidateSceneId]: data.image_url }));
        }
      } catch (e) {
        if (!cancelled) setComposeError(String(e.message || e));
      } finally {
        if (!cancelled) setComposing(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlinthCandidate, artworkId, candidateSceneId]);

  if (!ROOM_SCENES.length) return null;
  const scene = ROOM_SCENES[Math.min(sceneIdx, ROOM_SCENES.length - 1)];
  const isPlinth = scene?.type === "plinth";
  const isScreen = scene?.type === "screen";
  const prev = () => setSceneIdx((i) => (i - 1 + ROOM_SCENES.length) % ROOM_SCENES.length);
  const next = () => setSceneIdx((i) => (i + 1) % ROOM_SCENES.length);

  // v1.44 — real-angle images for sculptures. Only meaningful in the
  // fallback (non-composited) render — the composite already includes
  // the sculpture as a single flat image, so we hide the slider when
  // a composite is in use.
  const angleImages = (() => {
    if (!isPlinth) return null;
    const extras = Array.isArray(gallery)
      ? gallery.filter((u) => u && u !== artworkUrl)
      : [];
    const all = [artworkUrl, ...extras];
    return all.length >= 2 ? all : null;
  })();
  const hasAngles = !!angleImages;

  const compositeUrl = isPlinth ? composites[scene.id] : null;

  return (
    <section data-testid="room-viewer" className="mt-16 border-t border-[#E5E0D8] pt-12">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
        <div>
          <p className="label-eyebrow text-[#1E3A8A] flex items-center gap-2">
            <Home className="w-3.5 h-3.5" />— See it in your space
          </p>
          <h2 className="font-serif-display text-3xl text-[#111] mt-1">
            {scene.label}
          </h2>
        </div>
        <p className="font-mono-aam text-xs text-[#555]">
          {sceneIdx + 1} / {ROOM_SCENES.length}
        </p>
      </div>

      {/* Main scene — sized to each photo's natural aspect so the slot
          coordinates land precisely on the wall area without letterboxing. */}
      <div className="relative bg-[#F4EFE6]">
      <div className="relative">
        <RoomScene
          artworkUrl={artworkUrl}
          artworkAlt={artworkTitle}
          scene={scene}
          frameId={frameId}
          className="w-full"
          loading="eager"
          style={{ aspectRatio: String(scene.imageAspect || 4 / 3) }}
          showScaleRef={isPlinth && showScale}
          rotateDeg={isPlinth ? rotate : 0}
          dimensions={dimensions}
          angleImages={compositeUrl ? null : angleImages}
          compositeUrl={compositeUrl}
        />
        {isPlinth && composing && !compositeUrl && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px]"
            data-testid="compositor-loading"
          >
            <div className="flex flex-col items-center gap-3 text-[#111]">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-sm font-mono-aam tracking-wide">
                Placing sculpture into {scene.label.split("·")[0].trim().toLowerCase()}…
              </p>
              <p className="text-xs text-[#555] max-w-xs text-center">
                First look takes ~20 seconds while we generate a photoreal render. Subsequent views load instantly.
              </p>
            </div>
          </div>
        )}
        {isPlinth && composeError && (
          <div
            className="absolute bottom-2 left-2 bg-red-600/90 text-white text-[10px] px-2 py-1 tracking-wide"
            data-testid="compositor-error"
          >
            Photoreal render unavailable — showing flat placement.
          </div>
        )}
      </div>
        {/* Prev / Next arrows */}
        <button
          type="button"
          onClick={prev}
          data-testid="room-prev"
          aria-label="Previous room"
          className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-[#E5E0D8] p-2 backdrop-blur-sm transition"
        >
          <ChevronLeft className="w-4 h-4 text-[#111]" />
        </button>
        <button
          type="button"
          onClick={next}
          data-testid="room-next"
          aria-label="Next room"
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-[#E5E0D8] p-2 backdrop-blur-sm transition"
        >
          <ChevronRight className="w-4 h-4 text-[#111]" />
        </button>
      </div>

      {/* Scene controls — different per scene type */}
      <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
        {isPlinth ? (
          <div className="flex flex-wrap items-center gap-5">
            <p className="label-eyebrow text-[#555]">— Setting</p>
            {/* Context toggle — Outdoor · Museum · Home */}
            <div className="flex items-center gap-0 border border-[#E5E0D8]" data-testid="sculpture-context-toggle">
              {ROOM_SCENES.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSceneIdx(i)}
                  data-testid={`context-${s.id}`}
                  className={`px-4 py-1.5 text-xs uppercase tracking-wider transition ${
                    i === sceneIdx
                      ? "bg-[#111] text-white"
                      : "bg-white text-[#555] hover:text-[#111]"
                  }`}
                >
                  {s.label.split("·")[0].trim()}
                </button>
              ))}
            </div>
            {/* Scale reference toggle */}
            <label
              className="flex items-center gap-2 text-xs uppercase tracking-wider cursor-pointer select-none text-[#555]"
              data-testid="plinth-scale-toggle-label"
            >
              <input
                type="checkbox"
                checked={showScale}
                onChange={(e) => setShowScale(e.target.checked)}
                data-testid="plinth-scale-toggle"
                className="accent-[#1E3A8A]"
              />
              Scale ref (~1.7 m)
            </label>
            {/* Rotation slider — real angle swap when uploads exist, else CSS Y-rotate */}
            <label className="flex items-center gap-3 text-xs uppercase tracking-wider text-[#555]">
              {hasAngles ? `View (${angleImages.length} angles)` : "Rotate"}
              <input
                type="range"
                min="-60"
                max="60"
                step="5"
                value={rotate}
                onChange={(e) => setRotate(Number(e.target.value))}
                data-testid="plinth-rotate-slider"
                className="w-40 accent-[#1E3A8A]"
              />
              <span className="font-mono-aam text-[#111] w-14 text-right">
                {hasAngles
                  ? `${Math.min(angleImages.length, Math.max(1, Math.round(((rotate + 60) / 120) * (angleImages.length - 1)) + 1))}/${angleImages.length}`
                  : `${rotate > 0 ? "+" : ""}${rotate}°`}
              </span>
              <button
                type="button"
                onClick={() => setRotate(0)}
                data-testid="plinth-rotate-reset"
                className="text-[#1E3A8A] hover:underline"
              >
                Reset
              </button>
            </label>
          </div>
        ) : isScreen ? (
          <p className="label-eyebrow text-[#555]" data-testid="screen-caption">
            — On a digital display  ·  colour approximation
          </p>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <p className="label-eyebrow text-[#555]">— Frame</p>
            {FRAME_STYLES.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFrameId(f.id)}
                data-testid={`frame-${f.id}`}
                className={`flex items-center gap-2 px-3 py-1.5 border text-xs uppercase tracking-wider transition ${
                  frameId === f.id
                    ? "border-[#111] bg-[#111] text-white"
                    : "border-[#E5E0D8] bg-white text-[#555] hover:border-[#111]"
                }`}
              >
                {f.id !== "none" && (
                  <span
                    className="inline-block w-3 h-3 border"
                    style={{
                      backgroundColor: f.moulding,
                      borderColor: f.mouldingDark,
                    }}
                  />
                )}
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Scene thumbnail strip */}
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="room-thumbnails">
          {ROOM_SCENES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSceneIdx(i)}
              data-testid={`room-thumb-${s.id}`}
              aria-label={`View in ${s.label}`}
              className={`relative shrink-0 w-16 h-12 border transition ${
                i === sceneIdx ? "border-[#111]" : "border-[#E5E0D8] hover:border-[#1E3A8A]"
              }`}
            >
              <img
                src={s.image}
                alt={s.label}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-[#555] mt-4 max-w-2xl">
        {isPlinth
          ? (hasAngles
              ? `Mockups for visualisation only — the scale silhouette is approximately 1.7 m for reference. The view slider swaps between ${angleImages.length} real angles uploaded by the artist. Contact the studio for accurate viewing angles and shipping crates.`
              : "Mockups for visualisation only — the scale silhouette is approximately 1.7 m for reference. Rotation is a visual preview; ask the artist for extra angle photos to enable true angle-swapping.")
          : isScreen
          ? "Mockups for visualisation only — actual display size, bezel style and screen calibration vary. Contact the studio for the digital file, edition details and playback specifications."
          : "Mockups for visualisation only — frame profiles, mat colour and exact placement vary by framer. Contact the studio to discuss bespoke framing before purchase."}
      </p>
    </section>
  );
}
